import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { TrendBadge } from './TrendBadge'
import { type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface KpiCardProps {
  title: string
  value: string | number
  unit?: string
  change?: number | null
  changePeriod?: string
  icon?: LucideIcon
  loading?: boolean
  /** Pass true when a rising value is bad (e.g. error rate, churn rate) */
  invertTrend?: boolean
  className?: string
}

export function KpiCard({
  title,
  value,
  unit,
  change,
  changePeriod = 'vs เดือนก่อน',
  icon: Icon,
  loading = false,
  invertTrend = false,
  className,
}: KpiCardProps) {
  if (loading) {
    return (
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-4 rounded" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-24 mb-1" />
          <Skeleton className="h-3 w-32" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </CardHeader>
      <CardContent>
        <div className={cn('text-2xl font-bold', !unit && 'mb-0')}>
          {typeof value === 'number' ? value.toLocaleString() : value}
          {unit && <span className="text-base font-normal text-muted-foreground ml-1">{unit}</span>}
        </div>
        {change !== undefined && change !== null ? (
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <TrendBadge value={change} invertColor={invertTrend} />
            <span>{changePeriod}</span>
          </p>
        ) : (
          <p className="text-xs text-muted-foreground mt-1">&nbsp;</p>
        )}
      </CardContent>
    </Card>
  )
}
